import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { OverlayService, SignalButton, State } from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { PageHeaderComponent } from '@core/components/page-header/page-header.component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type {
  ListWorkspaceInvitationsApiResponse,
  ListWorkspaceMembersApiResponse,
  WorkspaceInvitationListItemResponse,
  WorkspaceMemberListItemResponse,
} from '@core/api/workspace/models';
import type { CreatedWorkspaceInvitationResponse } from '@core/api/workspace/models/createdWorkspaceInvitationResponse';
import type { SkippedWorkspaceInvitationResponse } from '@core/api/workspace/models/skippedWorkspaceInvitationResponse';
import {
  InviteMemberOverlayComponent,
  type InviteMemberOverlayResult,
} from '../invite-member-overlay/invite-member-overlay';

@Component({
  selector: 'app-team-members-page',
  imports: [DatePipe, PageHeaderComponent, SignalButton],
  templateUrl: './team-members-page.html',
  styleUrl: './team-members-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamMembersPage extends BaseComponent implements OnInit {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly overlayService = inject(OverlayService);

  readonly pendingState = new State<ListWorkspaceInvitationsApiResponse>();
  readonly membersState = new State<ListWorkspaceMembersApiResponse>();

  readonly pendingInvitations = signal<WorkspaceInvitationListItemResponse[]>([]);
  readonly members = signal<WorkspaceMemberListItemResponse[]>([]);
  readonly createdInvitations = signal<CreatedWorkspaceInvitationResponse[]>([]);
  readonly skippedInvitations = signal<SkippedWorkspaceInvitationResponse[]>([]);

  ngOnInit(): void {
    this.loadPendingInvitations();
    this.loadMembers();
  }

  openInviteMember(): void {
    const dialogRef = this.overlayService.openModal(InviteMemberOverlayComponent, {
      panelClass: ['invite-member-overlay-panel'],
    });

    dialogRef.closed.subscribe((result: InviteMemberOverlayResult | null | undefined) => {
      if (!result) {
        return;
      }

      this.createdInvitations.set(result.invitations);
      this.skippedInvitations.set(result.skipped);
      this.loadPendingInvitations();
    });
  }

  copyInviteLink(link: string): void {
    navigator.clipboard.writeText(link).then(
      () => this.toasterService.success('Invite link copied.'),
      () => this.toasterService.error('Could not copy link.'),
    );
  }

  formatRole(role: string): string {
    return role.charAt(0) + role.slice(1).toLowerCase();
  }

  private loadPendingInvitations(): void {
    this.executeRequest({
      state: this.pendingState,
      request: this.userAPIService.listWorkspaceInvitations(),
      onSuccess: (response) => {
        this.pendingInvitations.set(response.data.invitations);
      },
    });
  }

  private loadMembers(): void {
    this.executeRequest({
      state: this.membersState,
      request: this.userAPIService.listWorkspaceMembers(),
      onSuccess: (response) => {
        this.members.set(response.data.members);
      },
    });
  }
}
